import { useEffect, useState } from "react";
import { Grid, Box, Button, CircularProgress, Paper } from "@mui/material";
import PageHeader from "../common/PageHeader";
import { blobFromRequest, Body1, PrimaryButton, InlineButton, downloadUrl, NotFoundPage } from "@coherehealth/common";
import { useSnackbar } from "notistack";
import { useDownloadUsersAsCsv, useBatchUpdateUserInfo, UserManagementResponse } from "@coherehealth/qm-api";
import { useAuthorized } from "Authorization";
import { error as logError } from "logger";
import { UploadCompleteDrawer } from "./UploadCompleteDrawer";

const MAX_FILE_SIZE = 104857600;

export default function UserManagement() {
  const isQMManager = useAuthorized("QM_MANAGER");
  const [file, setFile] = useState<File>();
  const { enqueueSnackbar } = useSnackbar();
  const { refetch: downloadUsersAsCsv } = useDownloadUsersAsCsv({ lazy: true });
  const { mutate: uploadUserCsv, loading: uploadingUserCsv, error: errorUploadingUserCsv } = useBatchUpdateUserInfo({});
  const [uploadUserCsvResponse, setUploadUserCsvResponse] = useState<UserManagementResponse | null>();
  const [openResultsDrawer, setOpenResultsDrawer] = useState<boolean>(false);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>, id?: string) => {
    const input = e.target;
    const file = input?.files?.[0];
    if (file) {
      try {
        assertIsFile(file);
      } catch {
        enqueueSnackbar("Not a File", { variant: "error" });
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        enqueueSnackbar("Selected file is too large", { variant: "error" });
        return;
      }
      setFile(file);
    }
  };

  const resetInputValue = (event: React.MouseEvent<HTMLInputElement>) => {
    const element = event.currentTarget;
    element.value = "";
  };

  const handleUpload = async () => {
    if (file) {
      const formData = new FormData();
      formData.set("file", file);
      const response = await uploadUserCsv(formData as unknown as void);
      setUploadUserCsvResponse(response);
      setOpenResultsDrawer(true);
      setFile(undefined);
    }
  };

  useEffect(() => {
    if (errorUploadingUserCsv) {
      const error: any = errorUploadingUserCsv;
      const message = error?.data?.message;
      const embeddedError = error?.data?._embedded?.errors?.map((error: any) => error?.message).join("\n");
      const errorMessage = (
        <Box>
          <Box>Error uploading/processing csv</Box>
          {embeddedError ? <Box>{embeddedError}</Box> : message && <Box>{message}</Box>}
        </Box>
      );
      enqueueSnackbar(errorMessage, { variant: "error" });
      logError(`Error uploading/processing csv ${message} ${embeddedError}`);
      setFile(undefined);
    }
  }, [errorUploadingUserCsv, enqueueSnackbar]);

  if (!isQMManager) {
    return <NotFoundPage />;
  }

  return (
    <>
      <Grid
        container
        sx={{ padding: 4, flexDirection: "column", overflow: "hidden" }}
        data-testid="user-management-container"
      >
        <Grid item>
          <PageHeader headerLabel="User Management QM" />
        </Grid>

        <Grid item sx={{ display: "flex", width: "100%", justifyContent: "center" }}>
          <Grid sx={{ display: "flex", flexDirection: "column" }}>
            <Box sx={{ mt: "24px" }}>
              <PrimaryButton
                color="primary"
                onClick={async () => {
                  const blob = await blobFromRequest(downloadUsersAsCsv({}));
                  if (blob?.type.startsWith("text/csv")) {
                    downloadUrl(URL.createObjectURL(blob), "users.csv");
                  }
                }}
              >
                Download existing user collection
              </PrimaryButton>
            </Box>

            <Paper elevation={1} sx={{ height: "120px", width: "1080px", mt: 8 }}>
              <Box display="flex" alignItems="center" height="100%" justifyContent="space-evenly">
                <Box>
                  <Body1>Upload a .csv file to update users' info</Body1>
                </Box>
                <Box ml={2}>
                  <Button variant="contained" component="label" color="primary" sx={{ width: "166px", height: "48px" }}>
                    Browse
                    <input type="file" hidden accept=".csv" onChange={onFileChange} onClick={resetInputValue} />
                  </Button>
                </Box>
                <Box ml={2}>
                  <Body1>{file?.name || "No file selected"}</Body1>
                </Box>
                <Box ml={2}>
                  {uploadingUserCsv ? (
                    <CircularProgress size={24} />
                  ) : (
                    <InlineButton title="Upload" onClick={handleUpload} disabled={!file}>
                      Upload
                    </InlineButton>
                  )}
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Grid>
      <UploadCompleteDrawer
        open={openResultsDrawer}
        handleClose={() => setOpenResultsDrawer(false)}
        userManagementResponse={uploadUserCsvResponse ?? {}}
        transactionError={uploadingUserCsv ? null : errorUploadingUserCsv}
      />
    </>
  );
}

function assertIsFile(ipt: File | string | null): asserts ipt is File {
  if (!ipt || typeof ipt === "string") {
    throw new Error("Not a file");
  }
}
